/* Core CSS required for Ionic components to work properly */
import { IonApp, setupIonicReact } from '@ionic/react'
import '@ionic/react/css/core.css'
import '@ionic/react/css/display.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/float-elements.css'
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css'
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/typography.css'
import React, { useEffect } from 'react'
import './App.scss'
import ModalRenderer from './ModalRenderer'
/* Theme variables */
import { IonReactRouter } from '@ionic/react-router'
import { ThemeProvider } from '@mui/material'
import { useTranslation } from 'react-i18next'
import useAnalytics from './analytics/hooks/useAnalytics'
import { AppContent } from './components/AppContent'
import useUser from './contexts/hooks/useUser'
import HvacContextProvider from './pages/devices/hvac/HvacContext'
import BackButtonListener from './pages/home/components/BackButtonListener'
import useKeyboardScrollFix from './hooks/useKeyboardScrollFix'
import useMuiTheme from './styles/useMuiTheme'
import './theme/variables.css'
import usePreference from './utils/preferences/usePreference'

setupIonicReact({
	mode: 'md',
})

const App: React.FC = () => {
	const { user, userSettings } = useUser()

	const { i18n } = useTranslation()
	const theme = useMuiTheme()
	const [storedTheme, setStoredTheme] = usePreference('preferredTheme', 'system')
	useAnalytics()
	useKeyboardScrollFix()

	useEffect(() => {
		const handleSystemThemeChange = (e: MediaQueryListEvent | MediaQueryList) => {
			if (!userSettings || userSettings.themeMode === 'system' || !userSettings.themeMode) {
				e.matches ? document.body.classList.add('dark') : document.body.classList.remove('dark')
			}
		}

		const systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)')

		const loadTheme = async () => {
			if (userSettings) {
				const themeMode = userSettings.themeMode
				setStoredTheme(themeMode ?? 'system')

				switch (themeMode) {
					case 'dark':
						document.body.classList.add('dark')
						break
					case 'light':
						document.body.classList.remove('dark')
						break
					case 'system':
						handleSystemThemeChange(systemDarkQuery)
						break
				}
			} else if (storedTheme && ['dark', 'light'].includes(storedTheme)) {
				storedTheme === 'dark'
					? document.body.classList.add('dark')
					: document.body.classList.remove('dark')
			} else {
				handleSystemThemeChange(systemDarkQuery)
			}
		}

		loadTheme()

		systemDarkQuery.addEventListener('change', handleSystemThemeChange)
		return () => systemDarkQuery.removeEventListener('change', handleSystemThemeChange)
	}, [userSettings])

	useEffect(() => {
		i18n.changeLanguage(user?.language)
	}, [user?.language])

	return (
		<IonApp>
			<IonReactRouter>
				<HvacContextProvider>
					<ThemeProvider theme={theme}>
						<BackButtonListener />
						<ModalRenderer />
						<AppContent />
					</ThemeProvider>
				</HvacContextProvider>
			</IonReactRouter>
		</IonApp>
	)
}

export default App
